// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/components/listas/CabecalhoOrdenavel.tsx
//  Ordenação das listas pelo cabeçalho: clicar na coluna já ordenada inverte
//  o sentido; outra coluna começa no sentido padrão dela. Ordena no SERVIDOR.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { TableHead } from '@/components/ui/table';
import { cn } from '@/lib/utils';

export interface Ordenacao<C extends string> {
  ordenarPor: C;
  decrescente: boolean;
  /** Alterna a coluna. `decrescentePadrao`: sentido ao entrar numa coluna nova (datas: true). */
  ordenarPela: (coluna: C, decrescentePadrao?: boolean) => void;
}

export function useOrdenacao<C extends string>(colunaInicial: C, decrescenteInicial = true, onChange?: () => void): Ordenacao<C> {
  const [ordenarPor, setOrdenarPor] = useState<C>(colunaInicial);
  const [decrescente, setDecrescente] = useState(decrescenteInicial);
  return {
    ordenarPor, decrescente,
    ordenarPela: (coluna, decrescentePadrao = true) => {
      if (coluna === ordenarPor) setDecrescente((d) => !d);
      else { setOrdenarPor(coluna); setDecrescente(decrescentePadrao); }
      onChange?.();
    },
  };
}

interface CabecalhoOrdenavelProps<C extends string> {
  coluna: C;
  ordenacao: Ordenacao<C>;
  children: React.ReactNode;
  /** Sentido ao clicar pela 1ª vez nesta coluna (padrão: decrescente). */
  decrescentePadrao?: boolean;
  className?: string;
}

export function CabecalhoOrdenavel<C extends string>({
  coluna, ordenacao, children, decrescentePadrao = true, className,
}: CabecalhoOrdenavelProps<C>) {
  const ativa = ordenacao.ordenarPor === coluna;
  const Icone = !ativa ? ArrowUpDown : ordenacao.decrescente ? ArrowDown : ArrowUp;
  return (
    <TableHead className={className} aria-sort={ativa ? (ordenacao.decrescente ? 'descending' : 'ascending') : undefined}>
      <button type="button" className={cn('inline-flex items-center hover:text-foreground', className?.includes('text-right') && 'ml-auto')}
        onClick={() => ordenacao.ordenarPela(coluna, decrescentePadrao)}>
        {children}
        <Icone className={cn('h-3.5 w-3.5 ml-1', !ativa && 'opacity-40')} />
      </button>
    </TableHead>
  );
}
